import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit3, Trash2, Save, X, Crown, Users, Building } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const MODULES = ['leads', 'tasks', 'sales', 'service', 'spares'];
const MODULE_LABELS = { leads: 'Leads', tasks: 'Tasks', sales: 'Sales', service: 'Service', spares: 'Spares' };
const DURATION_TYPES = [
  { value: 'trial', label: 'Free Trial', days: 7 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'quarterly', label: 'Quarterly (3 mo)', days: 90 },
  { value: 'annual', label: 'Annual (12 mo)', days: 365 },
];
const formatINR = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt);

export default function AdminSubscriptionPlans({ user }) {
  const navigate = useNavigate();
  const [plans, setPlans] = React.useState([]);
  const [subs, setSubs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editPlan, setEditPlan] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('plans');
  const [activateModal, setActivateModal] = React.useState(null);

  React.useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [plResp, subResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription/plans/all`, getAuth()),
        axios.get(`${BACKEND_URL}/api/subscription/all-companies`, getAuth()),
      ]);
      setPlans(plResp.data.plans || []);
      setSubs(subResp.data.subscriptions || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSavePlan = async () => {
    try {
      if (editPlan.id) {
        await axios.put(`${BACKEND_URL}/api/subscription/plans/${editPlan.id}`, editPlan, getAuth());
        toast.success('Plan updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/subscription/plans`, editPlan, getAuth());
        toast.success('Plan created');
      }
      setEditPlan(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm('Deactivate this plan?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/subscription/plans/${id}`, getAuth());
      toast.success('Plan deactivated');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const handleAdminActivate = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/subscription/admin-activate`, activateModal, getAuth());
      toast.success('Subscription activated!');
      setActivateModal(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const newPlan = () => ({
    name: '', duration_type: 'monthly', duration_days: 30, base_price_per_month: 0,
    discount_percent: 0, modules: [], included_users: 5, extra_user_price_per_month: 500,
    limits: { max_leads: null, max_bookings: null }, is_trial: false, sort_order: 99,
  });

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/modules')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
            <Crown className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-gray-800">Subscription Management</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setActiveTab('plans')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'plans' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="tab-plans">Plans</button>
            <button onClick={() => setActiveTab('subs')} className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab === 'subs' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid="tab-subs">Company Subscriptions</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* ═══ PLANS TAB ═══ */}
        {activeTab === 'plans' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Subscription Plans ({plans.length})</h2>
              <button onClick={() => setEditPlan(newPlan())} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2" data-testid="create-plan-btn"><Plus className="w-4 h-4" /> Create Plan</button>
            </div>
            <div className="grid gap-4">
              {plans.map(p => (
                <div key={p.id} className={`bg-white rounded-xl border p-5 ${!p.is_active ? 'opacity-50' : ''}`} data-testid={`plan-row-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-gray-800">{p.name}</h3>
                        {p.is_trial && <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">TRIAL</span>}
                        {!p.is_active && <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full">INACTIVE</span>}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span>{p.duration_type} ({p.duration_days} days)</span>
                        <span>{formatINR(p.base_price_per_month)}/mo</span>
                        {p.discount_percent > 0 && <span className="text-green-600 font-medium">{p.discount_percent}% off</span>}
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{p.included_users} users</span>
                      </div>
                      <div className="flex gap-1.5 mt-2">
                        {p.modules.map(m => <span key={m} className="px-2 py-0.5 bg-gray-100 rounded text-[10px] font-medium text-gray-600">{MODULE_LABELS[m]}</span>)}
                      </div>
                      {p.pricing && <p className="text-xs text-gray-400 mt-1">Total: {formatINR(p.pricing.total)} (incl. GST)</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditPlan({ ...p })} className="p-2 hover:bg-gray-100 rounded-lg" data-testid={`edit-plan-${p.id}`}><Edit3 className="w-4 h-4 text-gray-500" /></button>
                      {p.is_active && <button onClick={() => handleDeletePlan(p.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ SUBSCRIPTIONS TAB ═══ */}
        {activeTab === 'subs' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Company Subscriptions ({subs.length})</h2>
              <button onClick={() => setActivateModal({ company_id: '', plan_id: plans[0]?.id || '', extra_users: 0, end_date: '' })} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2" data-testid="admin-activate-btn"><Plus className="w-4 h-4" /> Manual Activate</button>
            </div>
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Plan</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Activated By</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {subs.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50" data-testid={`sub-row-${s.id}`}>
                      <td className="px-4 py-3 flex items-center gap-2"><Building className="w-4 h-4 text-gray-400" />{s.company_name || s.company_id?.substring(0, 8)}</td>
                      <td className="px-4 py-3">{s.plan_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.status === 'active' ? 'bg-green-100 text-green-700' : s.status === 'trial' ? 'bg-blue-100 text-blue-700' : s.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{s.start_date} → {s.end_date}</td>
                      <td className="px-4 py-3 font-medium">{formatINR(s.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{s.activated_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ═══ EDIT/CREATE PLAN MODAL ═══ */}
      {editPlan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditPlan(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="plan-modal">
            <div className="px-5 py-3 border-b bg-green-700 text-white rounded-t-2xl flex items-center justify-between sticky top-0">
              <span className="font-semibold">{editPlan.id ? 'Edit Plan' : 'Create Plan'}</span>
              <button onClick={() => setEditPlan(null)} className="text-white/70 hover:text-white text-xl">&times;</button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="text-xs text-gray-500 mb-1 block">Plan Name</label><input type="text" value={editPlan.name} onChange={e => setEditPlan(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="plan-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Duration Type</label><select value={editPlan.duration_type} onChange={e => { const d = DURATION_TYPES.find(t => t.value === e.target.value); setEditPlan(p => ({ ...p, duration_type: e.target.value, duration_days: d?.days || 30, is_trial: e.target.value === 'trial' })); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="plan-duration">{DURATION_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}</select></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Duration (days)</label><input type="number" value={editPlan.duration_days} onChange={e => setEditPlan(p => ({ ...p, duration_days: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Base Price / Month (INR)</label><input type="number" value={editPlan.base_price_per_month} onChange={e => setEditPlan(p => ({ ...p, base_price_per_month: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="plan-price" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Discount %</label><input type="number" value={editPlan.discount_percent} onChange={e => setEditPlan(p => ({ ...p, discount_percent: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Included Users</label><input type="number" value={editPlan.included_users} onChange={e => setEditPlan(p => ({ ...p, included_users: parseInt(e.target.value) || 5 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Extra User Price / Month</label><input type="number" value={editPlan.extra_user_price_per_month} onChange={e => setEditPlan(p => ({ ...p, extra_user_price_per_month: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              </div>
              <div><label className="text-xs text-gray-500 mb-2 block">Modules</label>
                <div className="flex flex-wrap gap-2">
                  {MODULES.map(m => (
                    <button key={m} onClick={() => setEditPlan(p => ({ ...p, modules: p.modules.includes(m) ? p.modules.filter(x => x !== m) : [...p.modules, m] }))} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${editPlan.modules.includes(m) ? 'bg-green-100 border-green-400 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`} data-testid={`plan-module-${m}`}>{MODULE_LABELS[m]}</button>
                  ))}
                </div>
              </div>
              {editPlan.is_trial && (
                <div className="grid grid-cols-2 gap-3 bg-blue-50 p-3 rounded-lg">
                  <div><label className="text-xs text-blue-600 mb-1 block">Max Leads (trial)</label><input type="number" value={editPlan.limits?.max_leads || ''} onChange={e => setEditPlan(p => ({ ...p, limits: { ...p.limits, max_leads: parseInt(e.target.value) || null } }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="null = unlimited" /></div>
                  <div><label className="text-xs text-blue-600 mb-1 block">Max Bookings (trial)</label><input type="number" value={editPlan.limits?.max_bookings || ''} onChange={e => setEditPlan(p => ({ ...p, limits: { ...p.limits, max_bookings: parseInt(e.target.value) || null } }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="null = unlimited" /></div>
                </div>
              )}
              <div><label className="text-xs text-gray-500 mb-1 block">Sort Order</label><input type="number" value={editPlan.sort_order} onChange={e => setEditPlan(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setEditPlan(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleSavePlan} className="px-5 py-2 bg-green-700 text-white text-sm rounded-xl hover:bg-green-600 flex items-center gap-1.5" data-testid="save-plan-btn"><Save className="w-4 h-4" /> Save Plan</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADMIN ACTIVATE MODAL ═══ */}
      {activateModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setActivateModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="activate-modal">
            <div className="px-5 py-3 border-b bg-blue-700 text-white rounded-t-2xl font-semibold">Manual Activate</div>
            <div className="p-5 space-y-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Company ID</label><input type="text" value={activateModal.company_id} onChange={e => setActivateModal(p => ({ ...p, company_id: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="8b5c5152-..." data-testid="activate-company-id" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Plan</label><select value={activateModal.plan_id} onChange={e => setActivateModal(p => ({ ...p, plan_id: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">{plans.filter(p => p.is_active && !p.is_trial).map(p => <option key={p.id} value={p.id}>{p.name} — {formatINR(p.pricing?.total || 0)}</option>)}</select></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Extra Users</label><input type="number" value={activateModal.extra_users} onChange={e => setActivateModal(p => ({ ...p, extra_users: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Custom End Date (optional)</label><input type="date" value={activateModal.end_date} onChange={e => setActivateModal(p => ({ ...p, end_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setActivateModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleAdminActivate} className="px-5 py-2 bg-blue-700 text-white text-sm rounded-xl hover:bg-blue-600" data-testid="admin-activate-submit">Activate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
